import {Hand, KeyRound, MicOff, MonitorUp, ShieldAlert, VideoOff, Volume1} from 'lucide-react';
import type {CSSProperties} from 'react';
import {useIntl} from 'react-intl';
import {VideoTrack, isTrackReference, type TrackReferenceOrPlaceholder} from '@livekit/components-react';
import {Track} from 'livekit-client';
import type {ParticipantView} from '@phonehq/rtc-core';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {useCallMember} from '../../groupCalls/components/callContext';
import {MemberAvatar} from '../components/MemberAvatar';
import {groupSurfaceMessages as m} from './groupMessages';

export type GroupTileVariant = 'bubble' | 'stage' | 'film' | 'pip';

interface GroupTileProps {
    readonly trackRef: TrackReferenceOrPlaceholder;
    readonly view: ParticipantView;
    readonly variant: GroupTileVariant;
    readonly speaking?: boolean;
    readonly showCameraOff?: boolean;
    readonly className?: string;
    readonly style?: CSSProperties;
}

const WEAK = new Set(['poor', 'lost']);

const AVATAR = {bubble: 32, stage: 96, film: 40, pip: 48} as const;

const SpeakingRing = ({active, thick}: {active: boolean; thick: boolean}) => (
    <span
        aria-hidden="true"
        data-testid="group-call-speaking-ring"
        className={`pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-150 ${thick ? 'shadow-[inset_0_0_0_3px_var(--phq-call-brand-focus)]' : 'shadow-[inset_0_0_0_2px_var(--phq-call-brand-focus)]'} ${active ? 'opacity-100' : 'opacity-0'}`}
    />
);

export const GroupTile = ({trackRef, view, variant, speaking = false, showCameraOff = false, className = '', style}: GroupTileProps) => {
    const intl = useIntl();
    const profile = useCallMember(view.userId);
    const screen = trackRef.source === Track.Source.ScreenShare;
    const publication = isTrackReference(trackRef) ? trackRef.publication : undefined;
    const hasVideo = !!publication && !publication.isMuted && !!publication.track;
    const name = view.isLocal ? intl.formatMessage(m.you) : profile.name;
    const ringed = speaking && !screen;
    const label = screen ? intl.formatMessage(gm.tileScreen, {name: profile.name}) : ringed && variant === 'stage' ? intl.formatMessage(m.speaking, {name}) : name;
    const videoPaused = variant === 'stage' && !screen && view.cameraEnabled && WEAK.has(view.connectionQuality);
    const compact = variant !== 'stage';
    return (
        <div
            data-testid="group-call-tile"
            data-speaking={ringed}
            data-local={view.isLocal}
            style={style}
            className={`relative flex items-center justify-center overflow-hidden ${variant === 'bubble' ? 'h-[84px] rounded-xl' : 'rounded-xl'} bg-[var(--phq-call-surface)] ${className}`}
        >
            {hasVideo && isTrackReference(trackRef) ? (
                <VideoTrack
                    trackRef={trackRef}
                    className={`h-full w-full ${screen ? 'object-contain' : 'object-cover'}`}
                    style={view.isLocal && !screen ? {transform: 'scaleX(-1)'} : undefined}
                />
            ) : (
                <div className="flex flex-col items-center gap-3.5">
                    <MemberAvatar userId={view.userId} name={profile.name} mxcAvatarUrl={profile.mxcAvatarUrl} size={AVATAR[variant]}/>
                    {showCameraOff && variant === 'stage' && (
                        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--phq-call-ink-muted)]">
                            <VideoOff size={14} aria-hidden="true"/>
                            {intl.formatMessage(m.cameraOff)}
                        </span>
                    )}
                </div>
            )}
            {videoPaused && (
                <span className="absolute left-1/2 top-1/2 inline-flex h-7 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-lg bg-[var(--phq-call-chip-scrim)] px-2.5 text-[13px] font-semibold">
                    <VideoOff size={14} className="text-[var(--phq-call-warning-ink)]" aria-hidden="true"/>
                    {intl.formatMessage(m.videoPaused)}
                </span>
            )}
            {view.handRaised && !screen && (
                <span className="absolute left-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-amber-400 text-black" title={intl.formatMessage(gm.tileHandRaised)}>
                    <Hand size={14} aria-label={intl.formatMessage(gm.tileHandRaised)}/>
                </span>
            )}
            {!screen && !view.encrypted && (
                <span className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-[var(--phq-call-danger)]" title={intl.formatMessage(gm.tileNotEncrypted)}>
                    <ShieldAlert size={13} aria-label={intl.formatMessage(gm.tileNotEncrypted)}/>
                </span>
            )}
            {!screen && view.encrypted && !view.hasKey && !view.isLocal && (
                <span className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-md bg-amber-500 text-black" title={intl.formatMessage(gm.tileWaitingForKeys)}>
                    <KeyRound size={13} aria-label={intl.formatMessage(gm.tileWaitingForKeys)}/>
                </span>
            )}
            <span
                className={`absolute bottom-1.5 left-1.5 inline-flex max-w-[calc(100%-12px)] items-center gap-1.5 rounded-lg bg-[var(--phq-call-chip-scrim)] text-xs font-semibold ${compact ? 'h-[22px] px-1.5' : 'bottom-2 left-2 h-6 px-2'}`}
            >
                {screen && <MonitorUp size={12} aria-hidden="true"/>}
                {ringed && variant !== 'bubble' && <Volume1 size={14} className="text-[var(--phq-call-brand-ink)]" aria-hidden="true"/>}
                <span className="truncate">{label}</span>
                {!screen && !view.microphoneEnabled && (
                    <span className={`inline-flex shrink-0 items-center justify-center rounded-md bg-[var(--phq-call-danger)] ${compact ? 'h-[18px] w-[18px]' : 'h-5 w-5'}`}>
                        <MicOff size={compact ? 11 : 12} aria-label={intl.formatMessage(gm.tileMuted)}/>
                    </span>
                )}
            </span>
            <SpeakingRing active={ringed} thick={variant === 'stage' || variant === 'pip'}/>
        </div>
    );
};

interface SelfViewProps {
    readonly trackRef: TrackReferenceOrPlaceholder;
    readonly speaking?: boolean;
    readonly className?: string;
    readonly style?: CSSProperties;
}

export const SelfView = ({trackRef, speaking = false, className = '', style}: SelfViewProps) => {
    const intl = useIntl();
    if (!isTrackReference(trackRef) || trackRef.publication.isMuted || !trackRef.publication.track) {
        return null;
    }
    return (
        <div
            role="img"
            aria-label={intl.formatMessage(m.selfView)}
            data-testid="group-call-self-view"
            style={style}
            className={`pointer-events-none overflow-hidden rounded-lg border border-solid border-white/25 bg-[var(--phq-call-surface)] phq-call-shadow-overlay ${className}`}
        >
            <VideoTrack trackRef={trackRef} className="h-full w-full object-cover" style={{transform: 'scaleX(-1)'}}/>
            <SpeakingRing active={speaking} thick={false}/>
        </div>
    );
};
